import { STORAGE_KEYS } from '../constants';

// UUID v4 generator (fallback if crypto.randomUUID is not available)
const generateUUID = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// API Configuration
const API_CONFIG = {
  SKAI_ONE_URL: process.env.REACT_APP_SKAI_ONE_URL,
  SKAI_NET_URL: process.env.REACT_APP_SKAI_NET_URL,
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'x-lang': 'en',
  },
  REQUEST_TIMEOUT: 30000, // 30 seconds
};

/**
 * Enhanced API Error class for better error handling
 */
class ApiError extends Error {
  constructor(message, status, code, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
    this.timestamp = new Date().toISOString();
  }

  /**
   * Create ApiError from response
   */
  static async fromResponse(response, defaultMessage = 'API request failed') {
    let errorData = null;
    let message = defaultMessage;
    let code = null;

    try {
      errorData = await response.json();
      message = errorData.message || errorData.error || message;
      code = errorData.code || null;
    } catch (parseError) {
      // If response is not JSON, use default message
      message = `${defaultMessage} (${response.status} ${response.statusText})`;
    }

    return new ApiError(message, response.status, code, errorData);
  }

  /**
   * Check if error is due to network issues
   */
  isNetworkError() {
    return (
      this.status === 0 ||
      this.message.includes('NetworkError') ||
      this.message.includes('fetch')
    );
  }

  /**
   * Check if error is due to authentication issues
   */
  isAuthError() {
    return this.status === 401 || this.status === 403;
  }

  /**
   * Check if error is due to server issues
   */
  isServerError() {
    return this.status >= 500;
  }

  /**
   * Check if error is due to client issues
   */
  isClientError() {
    return this.status >= 400 && this.status < 500;
  }
}

/**
 * HTTP Request wrapper with timeout support
 */
const fetchWithTimeout = async (
  url,
  options = {},
  timeout = API_CONFIG.REQUEST_TIMEOUT
) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new ApiError('Request timeout', 0, 'TIMEOUT');
    }
    throw error;
  }
};

/**
 * Centralized API Service Class
 */
class APIService {
  constructor() {
    this.loadingRequests = new Set();
    this.interceptors = {
      request: [],
      response: [],
    };
  }

  /**
   * Get authorization token from storage
   */
  getAuthToken() {
    try {
      return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    } catch (error) {
      console.warn('Failed to retrieve auth token:', error);
      return null;
    }
  }

  /**
   * Build headers for request
   */
  buildHeaders(additionalHeaders = {}, includeAuth = true) {
    const headers = {
      ...API_CONFIG.DEFAULT_HEADERS,
      'x-request-id': generateUUID(),
      ...additionalHeaders,
    };

    if (includeAuth) {
      const token = this.getAuthToken();
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
    }

    return headers;
  }

  /**
   * Build full URL
   */
  buildUrl(baseUrl, endpoint, params = {}) {
    const url = new URL(endpoint, baseUrl);

    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        url.searchParams.append(key, params[key]);
      }
    });

    return url.toString();
  }

  /**
   * Process response and handle common cases
   */
  async processResponse(response, url) {
    // Handle no content responses
    if (response.status === 204) {
      return null;
    }

    // Check if response is successful
    if (!response.ok) {
      throw await ApiError.fromResponse(response);
    }

    // Try to parse JSON response
    try {
      const data = await response.json();

      // Handle API responses with status codes in body
      if (data.code && data.code !== 'OK') {
        throw new ApiError(
          data.message || 'API request failed',
          response.status,
          data.code,
          data
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }

      // If JSON parsing fails, try to get text content
      try {
        const text = await response.text();
        return text;
      } catch (textError) {
        throw new ApiError(
          'Failed to parse response',
          response.status,
          'PARSE_ERROR'
        );
      }
    }
  }

  /**
   * Add request interceptor
   */
  addRequestInterceptor(interceptor) {
    this.interceptors.request.push(interceptor);
  }

  /**
   * Add response interceptor
   */
  addResponseInterceptor(interceptor) {
    this.interceptors.response.push(interceptor);
  }

  /**
   * Apply request interceptors
   */
  async applyRequestInterceptors(config) {
    let modifiedConfig = { ...config };

    for (const interceptor of this.interceptors.request) {
      try {
        modifiedConfig = await interceptor(modifiedConfig);
      } catch (error) {
        console.warn('Request interceptor error:', error);
      }
    }

    return modifiedConfig;
  }

  /**
   * Apply response interceptors
   */
  async applyResponseInterceptors(response, error = null) {
    let modifiedResponse = response;
    let modifiedError = error;

    for (const interceptor of this.interceptors.response) {
      try {
        const result = await interceptor(modifiedResponse, modifiedError);
        if (result) {
          modifiedResponse = result.response || modifiedResponse;
          modifiedError = result.error || modifiedError;
        }
      } catch (interceptorError) {
        console.warn('Response interceptor error:', interceptorError);
      }
    }

    if (modifiedError) {
      throw modifiedError;
    }

    return modifiedResponse;
  }

  /**
   * Core request method
   */
  async request(config) {
    const {
      baseUrl = API_CONFIG.SKAI_ONE_URL,
      endpoint,
      method = 'GET',
      data = null,
      params = {},
      headers = {},
      includeAuth = true,
      timeout = API_CONFIG.REQUEST_TIMEOUT,
      responseType = 'json',
      cache = 'default',
    } = config;

    // Apply request interceptors
    const modifiedConfig = await this.applyRequestInterceptors(config);

    const url = this.buildUrl(baseUrl, endpoint, params);
    const requestId = `${method}:${url}`;

    // Track loading state
    this.loadingRequests.add(requestId);

    try {
      const requestOptions = {
        method: method.toUpperCase(),
        cache,
        headers: this.buildHeaders(headers, includeAuth),
      };

      // Add body for methods that support it
      if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
        if (data instanceof FormData) {
          // Remove content-type header for FormData to let browser set it
          delete requestOptions.headers['Content-Type'];
          requestOptions.body = data;
        } else {
          requestOptions.body = JSON.stringify(data);
        }
      }

      const response = await fetchWithTimeout(url, requestOptions, timeout);

      if (responseType !== 'json') {
        if (!response.ok) {
          throw await ApiError.fromResponse(response);
        }
        const disposition = response.headers.get('content-disposition');
        const data = await response.text();
        const processedResponse = {
          data,
          headers: { 'content-disposition': disposition },
        };
        return await this.applyResponseInterceptors(processedResponse);
      }

      const processedResponse = await this.processResponse(response, url);

      // Apply response interceptors
      return await this.applyResponseInterceptors(processedResponse);
    } catch (error) {
      // Apply response interceptors for errors
      await this.applyResponseInterceptors(null, error);
      throw error;
    } finally {
      // Remove from loading state
      this.loadingRequests.delete(requestId);
    }
  }

  /**
   * GET request
   */
  async get(endpoint, params = {}, options = {}) {
    return this.request({
      endpoint,
      method: 'GET',
      params,
      ...options,
    });
  }

  /**
   * POST request
   */
  async post(endpoint, data = null, options = {}) {
    return this.request({
      endpoint,
      method: 'POST',
      data,
      ...options,
    });
  }

  /**
   * PUT request
   */
  async put(endpoint, data = null, options = {}) {
    return this.request({
      endpoint,
      method: 'PUT',
      data,
      ...options,
    });
  }

  /**
   * PATCH request
   */
  async patch(endpoint, data = null, options = {}) {
    return this.request({
      endpoint,
      method: 'PATCH',
      data,
      ...options,
    });
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    return this.request({
      endpoint,
      method: 'DELETE',
      ...options,
    });
  }

  /**
   * Upload file
   */
  async uploadFile(endpoint, file, additionalData = {}, options = {}) {
    const formData = new FormData();
    formData.append('file', file);

    // Add additional data to form
    Object.keys(additionalData).forEach(key => {
      formData.append(key, additionalData[key]);
    });

    return this.request({
      endpoint,
      method: 'POST',
      data: formData,
      ...options,
    });
  }

  /**
   * Check if there are any loading requests
   */
  isLoading() {
    return this.loadingRequests.size > 0;
  }

  /**
   * Get count of loading requests
   */
  getLoadingCount() {
    return this.loadingRequests.size;
  }

  /**
   * Cancel all pending requests (if AbortController is supported)
   */
  cancelAllRequests() {
    // Note: This is a simplified implementation
    // In a more sophisticated setup, you'd track AbortControllers
    this.loadingRequests.clear();
  }

  /**
   * SKAI One API request
   */
  async skaiOneRequest(endpoint, options = {}) {
    return this.request({
      baseUrl: API_CONFIG.SKAI_ONE_URL,
      endpoint,
      ...options,
    });
  }

  /**
   * SKAI Net API request
   */
  async skaiNetRequest(endpoint, options = {}) {
    return this.request({
      baseUrl: API_CONFIG.SKAI_NET_URL,
      endpoint,
      ...options,
    });
  }
}

// Create singleton instance
const apiService = new APIService();

// Add default response interceptor for token refresh
apiService.addResponseInterceptor(async (response, error) => {
  if (error instanceof ApiError && error.isAuthError() && error.status === 401) {
    // Token might be expired, attempt refresh
    // This will be handled by the auth service
    const event = new CustomEvent('token-expired', { detail: error });
    window.dispatchEvent(event);
  }

  // Return original response/error to continue normal flow
  if (error) {
    return { error };
  }
  return { response };
});

export default apiService;
export { ApiError, API_CONFIG };
